import { Component, computed, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { DashboardItem } from '../../../api/app-types/dashboard.app';
import { CLIENT_API } from '../../../api/injection-tokens/services.token';
import { LvrHeading } from '../../../core/components/heading/heading';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { ClientList } from '../client-list/client-list';

@Component({
	selector: 'lvr-client-view',
	imports: [TranslocoModule, MatIconModule, ClientList, LvrHeading],
	templateUrl: './client-view.html',
	styleUrl: './client-view.scss',
})
export class ClientView {
	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(CLIENT_API);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly representationMode = inject(REPRESENTATION_MODE);

	public readonly page = input.required<PaginatedResponse<DashboardItem>>();

	private readonly initialPage = computed<PaginatedResponse<DashboardItem>>(() => {
		const pagination = this.page();
		return {
			entities: pagination.entities,
			offset: pagination.offset,
			range: pagination.range ?? this.env.defaultPageSize,
			totalEntities: pagination.totalEntities,
		};
	});

	public readonly linkedPage = linkedSignal({
		source: this.initialPage,
		computation: (init) => ({
			...init,
			range: init.range || this.env.defaultPageSize,
		}),
	});

	public readonly clients = computed(() => this.linkedPage().entities);
	public readonly numberOfActiveAssignments = computed(
		() => this.linkedPage().entities[0]?.activeAssignmentForTheUserCount ?? 0,
	);
	public readonly loading = signal(false);
	private readonly lastRepresentationKey = signal<string | null>(null);
	private readonly representationKey = computed(() => {
		const isActive = this.representationMode.isActive();
		return isActive ? (this.representationMode.mode().representedUserId ?? '') : '';
	});
	private readonly currentPageSize = computed(
		() => this.linkedPage().range || this.page().range || this.env.defaultPageSize,
	);

	public constructor() {
		effect(() => {
			const key = this.representationKey();
			const pageSize = this.currentPageSize();

			if (this.lastRepresentationKey() === null) {
				this.lastRepresentationKey.set(key);

				if (key) {
					void this.reloadPage(0, pageSize);
				}
				return;
			}

			if (this.lastRepresentationKey() === key) return;

			this.lastRepresentationKey.set(key);
			void this.reloadPage(0, pageSize);
		});
	}

	public async onPageRequested(event: { pageIndex: number; pageSize: number }) {
		await this.reloadPage(event.pageIndex, event.pageSize);
	}

	private async reloadPage(pageIndex: number, pageSize: number) {
		this.loading.set(true);
		try {
			const userId = this.representationMode.isActive() ? this.representationMode.mode().representedUserId : undefined;
			const update = await this.api.getAllDashboard(pageIndex, pageSize, userId);
			this.linkedPage.set({ ...update, offset: pageIndex, range: pageSize });
		} catch {
			this.ntfy.addNotification(createErrorNotification('Fehler beim Neuladen der Aufträge'));
		} finally {
			this.loading.set(false);
		}
	}
}
